'use client'

import { useLiveVenuePayloads } from '@/service/venue-health.hook'
import { replayCachedTickers } from '@/service/ws/ticker-channel-cache'
import {
  createMultiStreamHook,
  createStreamHook,
  type MarketDataStreamPayload,
} from '@/service/ws/use-web-socket-with-proto'
import { TickerList } from '@/types/generated/common/types'

const multiStreamKeyCache = new WeakMap<MarketDataStreamPayload[], ReadonlySet<string>>()

function getSubscribedKeys(payloads: MarketDataStreamPayload[]): ReadonlySet<string> {
  const cached = multiStreamKeyCache.get(payloads)
  if (cached) return cached

  const keys = new Set(payloads.map((payload) => `${payload.exchange}:${payload.symbol}`))
  multiStreamKeyCache.set(payloads, keys)
  return keys
}

const useRawMarketDataStream = createStreamHook<MarketDataStreamPayload, TickerList>({
  channelKey: 'marketDataStream',
  extractResponse: (message, payload) => {
    const tickers = message.data?.tickers?.tickers.filter(
      (ticker) => ticker.symbol === payload.symbol && ticker.exchange === payload.exchange
    )
    return tickers?.length ? { tickers } : undefined
  },
  getPayloadDeps: (p) => [p.symbol, p.exchange, p.dataType],
  listenerChannelKey: 'marketDataStream:tickers',
  replay: replayCachedTickers,
})

const useRawMultiMarketDataStream = createMultiStreamHook<MarketDataStreamPayload, TickerList>({
  channelKey: 'marketDataStream',
  extractResponse: (message, payloads) => {
    const subscribedKeys = getSubscribedKeys(payloads)
    const tickers = message.data?.tickers?.tickers.filter((ticker) =>
      subscribedKeys.has(`${ticker.exchange}:${ticker.symbol}`)
    )
    return tickers?.length ? { tickers } : undefined
  },
  listenerChannelKey: 'marketDataStream:tickers',
  replay: replayCachedTickers,
})

export function useMarketDataStream(options: Parameters<typeof useRawMarketDataStream>[0]) {
  const connected = options.connected ?? true
  const payloads = useLiveVenuePayloads([options.payload], connected)
  return useRawMarketDataStream({
    ...options,
    connected: payloads.length > 0,
  })
}

export function useMultiMarketDataStream(
  options: Parameters<typeof useRawMultiMarketDataStream>[0]
) {
  const connected = options.connected ?? true
  const payloads = useLiveVenuePayloads(options.payloads, connected)
  return useRawMultiMarketDataStream({
    ...options,
    connected: payloads.length > 0,
    payloads,
  })
}
